package com.matchpredictor.web

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

@Serializable
enum class MatchOutcome { HOME_WIN, DRAW, AWAY_WIN }

@Serializable
enum class PollStatus { OPEN, CLOSED, SETTLED }

@Serializable
enum class PollSource { AUTO, MANUAL }

@Serializable
enum class MatchStatus { DRAFT, PUBLISHED, FINISHED }

@Serializable
enum class UserRole { USER, ADMIN }

/**
 * Неизвестные/устаревшие коды лиг не мапятся в EPL — десериализация падает;
 * списки опросов и матчей в [ApiClient] пропускают такие элементы.
 */
@Serializable(with = LeagueSerializer::class)
enum class League { EPL, SERIE_A, LA_LIGA, RPL, CHAMPIONSHIP, LIGUE_1, BUNDESLIGA }

object LeagueSerializer : KSerializer<League> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("League", PrimitiveKind.STRING)

    override fun serialize(encoder: Encoder, value: League) {
        encoder.encodeString(value.name)
    }

    override fun deserialize(decoder: Decoder): League {
        val raw = decoder.decodeString()
        return League.entries.firstOrNull { it.name.equals(raw, ignoreCase = true) }
            ?: throw IllegalArgumentException("Unknown league: $raw")
    }
}

@Serializable
data class LeagueDto(
    val code: String,
    val title: String,
    val subtitle: String
)

@Serializable
data class UserDto(
    val id: Long,
    val username: String,
    val displayName: String,
    val role: UserRole,
    val avatarIcon: String? = null,
    val mustChangePassword: Boolean = false,
    val email: String? = null,
    val emailVerified: Boolean = false
)

@Serializable
data class AuthResponse(val token: String, val user: UserDto)

@Serializable
data class SeasonDto(val id: Long, val name: String, val active: Boolean)

@Serializable
data class PollDto(
    val id: Long,
    val matchId: Long,
    val league: League = League.EPL,
    val homeTeam: String,
    val awayTeam: String,
    val kickoffAt: String,
    val status: PollStatus,
    val source: PollSource,
    val closesAt: String,
    val result: MatchOutcome? = null,
    val homeScore: Int? = null,
    val awayScore: Int? = null,
    val homePenScore: Int? = null,
    val awayPenScore: Int? = null,
    val myPrediction: MatchOutcome? = null,
    val votesHome: Int = 0,
    val votesDraw: Int = 0,
    val votesAway: Int = 0,
    val roundName: String? = null,
    val homeLogoUrl: String? = null,
    val awayLogoUrl: String? = null
)

@Serializable
data class RatingEntryDto(
    val rank: Int,
    val userId: Long,
    val username: String,
    val displayName: String,
    val correctPredictions: Int,
    val totalPredictions: Int,
    val accuracyPercent: Double,
    val avatarIcon: String? = null
)

@Serializable
data class AchievementDto(
    val code: String,
    val title: String,
    val description: String,
    val icon: String,
    val unlocked: Boolean,
    val unlockedAt: String? = null
)

@Serializable
data class MessageResponse(val message: String)

@Serializable
data class LoginRequest(val username: String, val password: String)

@Serializable
data class RegisterRequest(val username: String, val password: String, val displayName: String, val email: String)

@Serializable
data class TokenRequest(val token: String)

@Serializable
data class ForgotPasswordRequest(val email: String)

@Serializable
data class ResetPasswordRequest(val token: String, val newPassword: String)

@Serializable
data class UpdateEmailRequest(val email: String, val currentPassword: String)

@Serializable
data class ChangeTemporaryPasswordRequest(val newPassword: String)

@Serializable
data class AdminPasswordResetResponse(
    val user: UserDto,
    val temporaryPassword: String
)

@Serializable
enum class ChatMessageKind { TEXT, STICKER }

@Serializable
data class ChatReplyDto(
    val id: Long,
    val displayName: String,
    val body: String
)

@Serializable
data class ChatMessageDto(
    val id: Long,
    val userId: Long,
    val displayName: String,
    val avatarIcon: String? = null,
    val body: String,
    val kind: ChatMessageKind = ChatMessageKind.TEXT,
    val stickerCode: String? = null,
    val createdAt: String,
    val replyTo: ChatReplyDto? = null
)

@Serializable
data class SendChatMessageRequest(
    val body: String? = null,
    val stickerCode: String? = null,
    val replyToId: Long? = null
)

@Serializable
data class StickerDto(
    val code: String,
    val title: String,
    val icon: String,
    val unlockAt: Int,
    val unlocked: Boolean
)

@Serializable
data class StickersMineResponse(
    val chatMessageCount: Int,
    val items: List<StickerDto>
)

@Serializable
data class ChatStreamTicketResponse(
    val ticket: String,
    val expiresAt: String
)

@Serializable
data class PlacePredictionRequest(val pollId: Long, val choice: MatchOutcome)

@Serializable
data class SetAvatarRequest(val code: String)

@Serializable
data class CreateMatchRequest(
    val seasonId: Long,
    val league: League = League.EPL,
    val homeTeam: String,
    val awayTeam: String,
    val kickoffAt: String,
    val publish: Boolean = false
)

@Serializable
data class CreatePollRequest(val matchId: Long, val closesAt: String? = null)

@Serializable
data class MatchDto(
    val id: Long,
    val seasonId: Long,
    val league: League = League.EPL,
    val homeTeam: String,
    val awayTeam: String,
    val kickoffAt: String,
    val status: MatchStatus,
    val result: MatchOutcome? = null,
    val homeScore: Int? = null,
    val awayScore: Int? = null,
    val homePenScore: Int? = null,
    val awayPenScore: Int? = null,
    val roundName: String? = null,
    val homeLogoUrl: String? = null,
    val awayLogoUrl: String? = null
)

@Serializable
data class SettlePollRequest(val result: MatchOutcome)

@Serializable
data class FeedSyncResponse(
    val imported: Int,
    val settled: Int,
    val message: String
)

@Serializable
enum class NotificationType { PREDICTION_CORRECT, ADMIN_BROADCAST, INACTIVITY_REMINDER, STICKER_PACK }

@Serializable
enum class PushPlatform { WEB, ANDROID, IOS, HUAWEI }

@Serializable
enum class PushProvider { WEB_PUSH, FCM, HMS }

@Serializable
enum class NotificationCampaignStatus {
    DRAFT, SCHEDULED, RUNNING, PAUSED, CANCELLED, COMPLETED
}

@Serializable
enum class NotificationCampaignAudience { ALL, USER_IDS }

@Serializable
data class NotificationDto(
    val id: Long,
    val type: NotificationType,
    val title: String,
    val body: String,
    val payloadJson: String? = null,
    val createdAt: String,
    val readAt: String? = null,
    val campaignId: Long? = null
)

@Serializable
data class NotificationListResponse(
    val items: List<NotificationDto>,
    val nextBeforeId: Long? = null
)

@Serializable
data class UnreadCountResponse(val count: Long)

@Serializable
data class NotificationPreferencesDto(
    val pushPredictionCorrect: Boolean,
    val pushAdminBroadcast: Boolean,
    val pushInactivityReminder: Boolean = true,
    val pushStickerPack: Boolean = true
)

@Serializable
data class UpdateNotificationPreferencesRequest(
    val pushPredictionCorrect: Boolean? = null,
    val pushAdminBroadcast: Boolean? = null,
    val pushInactivityReminder: Boolean? = null,
    val pushStickerPack: Boolean? = null
)

@Serializable
data class PushRegisterRequest(
    val platform: PushPlatform,
    val provider: PushProvider,
    val endpoint: String? = null,
    val p256dh: String? = null,
    val auth: String? = null,
    val fcmToken: String? = null,
    val hmsToken: String? = null,
    val appId: String? = null,
    val appVersion: String? = null
)

@Serializable
data class VapidPublicKeyResponse(val publicKey: String)

@Serializable
data class NotificationSettingsDto(
    val pushesEnabled: Boolean,
    val adminBroadcastsEnabled: Boolean,
    val predictionCorrectPushEnabled: Boolean,
    val inactivityReminderEnabled: Boolean = true,
    val inactivityReminderDays: Int = 5,
    val stickerPackPushEnabled: Boolean = true
)

@Serializable
data class UpdateNotificationSettingsRequest(
    val pushesEnabled: Boolean? = null,
    val adminBroadcastsEnabled: Boolean? = null,
    val predictionCorrectPushEnabled: Boolean? = null,
    val inactivityReminderEnabled: Boolean? = null,
    val inactivityReminderDays: Int? = null,
    val stickerPackPushEnabled: Boolean? = null
)

@Serializable
data class CreateNotificationCampaignRequest(
    val title: String,
    val body: String,
    val payloadJson: String? = null,
    val audience: NotificationCampaignAudience = NotificationCampaignAudience.ALL,
    val userIds: List<Long> = emptyList(),
    val scheduledAt: String? = null
)

@Serializable
data class NotificationCampaignDto(
    val id: Long,
    val title: String,
    val body: String,
    val payloadJson: String? = null,
    val audience: NotificationCampaignAudience,
    val status: NotificationCampaignStatus,
    val scheduledAt: String? = null,
    val startedAt: String? = null,
    val finishedAt: String? = null,
    val targeted: Int,
    val enqueued: Int,
    val sent: Int,
    val failed: Int,
    val createdByAdminId: Long,
    val createdAt: String
)
